// Replay v2 tab: replays of real tasks from your own sessions on cheaper models. A dataset is a study: its page
// leads with the routing rules its evidence supports (which slice of work can move to which cheaper model, what it
// saves), then cost against quality, the leaderboard, its tasks and runs. v1 suites stay on the Task Replay tab.
// Every page is a shareable URL:
//
//   #/replay                                          the datasets, each with its headline rules
//   #/replay?id=<id>&base=<model>&margin=35&save=0.2  one dataset, for traffic on <model>, with a margin
//   #/replay?task=<task>[&id=<id>]                    one task (from a dataset: back goes to it)
//   #/replay?run=<run>&task=<task>&model=<model>      one result: a model's replay of a task in a run
import { state, $ } from '../core'
import { syncHash } from '../router'
import { renderDatasets, renderDataset } from './datasets'
import { renderResult } from './result'
import { renderTask } from './taskpage'
import { hideTip } from './tip'

export type ReplayState = typeof state.rp

const KEYS = ['id', 'base', 'margin', 'save', 'task', 'run', 'model'] as const

export function getReplayParams(): Record<string, string> {
  const q: Record<string, string> = {}
  for (const k of KEYS) if (state.rp[k]) q[k] = state.rp[k]
  return q
}

export function applyReplayParams(query: Record<string, string>): void {
  const page = (r: ReplayState) => [r.id, r.task, r.run, r.model].join('\n')
  const was = page(state.rp)
  state.rp = Object.fromEntries(KEYS.map((k) => [k, query[k] || ''])) as ReplayState
  if (page(state.rp) !== was) window.scrollTo(0, 0)  // another page (a link, back): from its top
  renderReplay()
}

/** Move within the tab: merge `next` into the state, mirror it into the URL, repaint (a new page from its top). */
export function goReplay(next: Partial<ReplayState>, opts?: { replace?: boolean }): void {
  state.rp = { ...state.rp, ...next }
  syncHash(opts)
  if (!opts?.replace) window.scrollTo(0, 0)
  renderReplay()
}

/** A section of the next page to scroll to once it is painted (the dataset's tasks, from a breadcrumb). */
let anchor = ''
export function takeAnchor(): string {
  const a = anchor
  anchor = ''
  return a
}

/** Open a page: the others' keys cleared, the dataset kept for the way back. */
export const openTask = (task: string) => goReplay({ task, run: '', model: '' })
export const openResult = (run: string, task: string, model: string) => goReplay({ run, task, model })
export const openDataset = (id: string, at = '') => {
  anchor = at
  goReplay({ id, task: '', run: '', model: '', base: '', margin: '', save: '' })
}

export function renderReplay(): void {
  const root = $('#replay') as HTMLElement | null
  if (!root) return
  hideTip()
  const r = state.rp
  if (r.run && r.task && r.model) renderResult(root, r.run, r.task, r.model)
  else if (r.task) renderTask(root, r.task)
  else if (r.id) renderDataset(root, r.id)
  else renderDatasets(root)
}
